import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Input,
  Radio,
  Tag,
  Alert,
  Descriptions,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  GlobalOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { internalPendingSignups } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';

const { TextArea } = Input;

// Stage 5N-12: SEZ pending-signup admin surface.
//
// SEZ customers (sez_unit / sez_developer) cannot reach the payment
// step until an internal admin both approves them AND commits a tax
// decision (regular_taxable @ 18% or sez_zero_rated @ 0%). This page
// is the operator's queue.
//
// Approve modal hard-couples treatment + percent so the admin cannot
// save "sez_zero_rated @ 18%". Backend re-validates the same rule
// (services/internalAdmin/pendingSignups.service.js).

function statusTag(s) {
  const map = {
    pending: { color: 'gold', icon: <ClockCircleOutlined />, label: 'pending' },
    approved: { color: 'green', icon: <CheckCircleOutlined />, label: 'approved' },
    rejected: { color: 'red', icon: <CloseCircleOutlined />, label: 'rejected' }
  };
  const m = map[s] || { color: 'default', label: s || 'unknown' };
  return (
    <Tag color={m.color} icon={m.icon}>
      {m.label}
    </Tag>
  );
}

function customerTypeLabel(t) {
  if (t === 'sez_unit') return 'SEZ Unit';
  if (t === 'sez_developer') return 'SEZ Developer';
  return t || '—';
}

export default function SezPendingSignupsPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const canApprove = hasPermission('accounts.sez.approve');
  const canReject = hasPermission('accounts.sez.reject');

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');

  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // Approve modal state
  const [taxTreatment, setTaxTreatment] = useState('regular_taxable');
  const [taxPercent, setTaxPercent] = useState(18);
  const [approveNote, setApproveNote] = useState('');

  // Reject modal state
  const [rejectReason, setRejectReason] = useState('');

  async function load(status = filterStatus) {
    setLoading(true);
    try {
      const resp = await internalPendingSignups.listSez({ status, limit: 100 });
      setItems(Array.isArray(resp.items) ? resp.items : []);
    } catch (err) {
      notifyError(err, { title: 'Could not load SEZ pending signups' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(filterStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  function openApprove(row) {
    setApproveTarget(row);
    // Default to regular_taxable @ 18% — the more conservative choice
    // for an SEZ customer who lacks proper LOA paperwork. Operator
    // flips to sez_zero_rated @ 0% if SEZ documentation checks out.
    setTaxTreatment('regular_taxable');
    setTaxPercent(18);
    setApproveNote('');
  }

  function openReject(row) {
    setRejectTarget(row);
    setRejectReason('');
  }

  // Hard-couple the radio + percent:
  //   sez_zero_rated -> always 0
  //   regular_taxable -> always 18 (operator can edit but never 0)
  function onTaxTreatmentChange(e) {
    const v = e.target.value;
    setTaxTreatment(v);
    if (v === 'sez_zero_rated') setTaxPercent(0);
    if (v === 'regular_taxable' && taxPercent === 0) setTaxPercent(18);
  }

  async function doApprove() {
    if (!approveTarget) return;
    if (taxTreatment === 'sez_zero_rated' && Number(taxPercent) !== 0) {
      notifyError(
        { code: 'SEZ_TAX_DECISION_INVALID' },
        { title: 'Tax decision invalid', description: 'sez_zero_rated must use 0%.' }
      );
      return;
    }
    if (taxTreatment === 'regular_taxable' && Number(taxPercent) === 0) {
      notifyError(
        { code: 'SEZ_TAX_DECISION_INVALID' },
        { title: 'Tax decision invalid', description: 'regular_taxable cannot be 0% — pick sez_zero_rated instead.' }
      );
      return;
    }
    setBusy(true);
    try {
      await internalPendingSignups.approveSezPayment(approveTarget.pendingSignupId, {
        taxTreatment,
        taxPercent: Number(taxPercent),
        note: approveNote || null
      });
      notifySuccess(
        'SEZ approved for payment',
        taxTreatment === 'sez_zero_rated'
          ? 'Customer can now pay with zero-rated GST.'
          : `Customer can now pay with ${taxPercent}% GST.`
      );
      setApproveTarget(null);
      load(filterStatus);
    } catch (err) {
      notifyError(err, { title: 'Could not approve SEZ payment' });
    } finally {
      setBusy(false);
    }
  }

  async function doReject() {
    if (!rejectTarget) return;
    const reason = (rejectReason || '').trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason for rejecting SEZ payment.' }
      );
      return;
    }
    setBusy(true);
    try {
      await internalPendingSignups.rejectSezPayment(rejectTarget.pendingSignupId, { reason });
      notifySuccess('SEZ payment rejected', 'The customer cannot proceed to payment.');
      setRejectTarget(null);
      load(filterStatus);
    } catch (err) {
      notifyError(err, { title: 'Could not reject SEZ payment' });
    } finally {
      setBusy(false);
    }
  }

  const columns = [
    {
      title: 'GSTIN',
      dataIndex: 'gstin',
      key: 'gstin',
      width: 170,
      render: (v) => <code className="text-xs">{v}</code>
    },
    {
      title: 'Legal name',
      dataIndex: 'legalName',
      key: 'legalName',
      ellipsis: true
    },
    {
      title: 'Customer type',
      dataIndex: 'customerTaxType',
      key: 'customerTaxType',
      width: 140,
      render: customerTypeLabel
    },
    {
      title: 'State',
      key: 'state',
      width: 200,
      render: (_v, row) =>
        row.gstStateName
          ? `${row.gstStateName} (${row.gstStateCode || '—'})`
          : (row.gstStateCode || '—')
    },
    {
      title: 'LOA / approval',
      dataIndex: 'sezApprovalNumber',
      key: 'sezApprovalNumber',
      width: 180,
      render: (v) => (v ? <code className="text-xs">{v}</code> : '—')
    },
    {
      title: 'Status',
      dataIndex: 'sezPaymentApprovalStatus',
      key: 'sezPaymentApprovalStatus',
      width: 130,
      render: statusTag
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v) => formatDateTime(v) || '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_v, row) => {
        const status = row.sezPaymentApprovalStatus;
        return (
          <Space size="small" wrap>
            <Tooltip title="View detail">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setViewTarget(row)}
              />
            </Tooltip>
            {status !== 'approved' ? (
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={!canApprove}
                onClick={() => openApprove(row)}
              >
                Approve
              </Button>
            ) : null}
            {status !== 'rejected' ? (
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                disabled={!canReject}
                onClick={() => openReject(row)}
              >
                Reject
              </Button>
            ) : null}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <GlobalOutlined /> SEZ Pending Approval
          </span>
        }
        subtitle="SEZ Unit / Developer signups awaiting admin tax decision before payment."
        actions={
          <Space>
            <Radio.Group
              size="middle"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: 'Pending', value: 'pending' },
                { label: 'Approved', value: 'approved' },
                { label: 'Rejected', value: 'rejected' }
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => load(filterStatus)}>
              Refresh
            </Button>
          </Space>
        }
      />

      {loading ? (
        <LoadingBlock tip="Loading SEZ pending signups…" />
      ) : (
        <Table
          rowKey="pendingSignupId"
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No SEZ signups in this state' }}
        />
      )}

      {/* ---- Approve modal ---- */}
      <Modal
        title="Approve SEZ for payment"
        open={!!approveTarget}
        confirmLoading={busy}
        onCancel={() => setApproveTarget(null)}
        onOk={doApprove}
        okText="Approve & unlock payment"
        cancelText="Cancel"
        width={600}
      >
        {approveTarget ? (
          <div>
            <Alert
              type="info"
              showIcon
              className="mb-4"
              message="Pick the tax treatment that will apply to this customer's first invoice."
              description={
                <span>
                  This decision freezes onto the invoice. SEZ customers
                  whose paperwork is complete should get{' '}
                  <strong>Zero-rated GST (0%)</strong>; SEZ customers
                  whose LOA paperwork is incomplete should get{' '}
                  <strong>Taxable GST @ 18%</strong> (refundable later
                  via a separate process).
                </span>
              }
            />
            <Descriptions size="small" column={1} bordered className="mb-4">
              <Descriptions.Item label="GSTIN">
                <code>{approveTarget.gstin}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Legal name">
                {approveTarget.legalName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Customer type">
                {customerTypeLabel(approveTarget.customerTaxType)}
              </Descriptions.Item>
              <Descriptions.Item label="LOA / approval">
                {approveTarget.sezApprovalNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Authorised operations">
                {approveTarget.sezAuthorisedOperationsConfirmed
                  ? <Tag color="green">Confirmed</Tag>
                  : <Tag color="red">Not confirmed</Tag>}
              </Descriptions.Item>
            </Descriptions>

            <div className="mb-3">
              <p className="font-semibold text-ink-900 mb-1">Tax treatment</p>
              <Radio.Group value={taxTreatment} onChange={onTaxTreatmentChange}>
                <Space direction="vertical">
                  <Radio value="regular_taxable">
                    Taxable GST @ 18% (regular GST treatment)
                  </Radio>
                  <Radio value="sez_zero_rated">
                    Zero-rated GST @ 0% (SEZ supply, authorised operations)
                  </Radio>
                </Space>
              </Radio.Group>
            </div>

            <div className="mb-3">
              <p className="font-semibold text-ink-900 mb-1">Tax percent</p>
              <Input
                type="number"
                min={0}
                max={40}
                step={0.5}
                value={taxPercent}
                disabled={taxTreatment === 'sez_zero_rated'}
                onChange={(e) => setTaxPercent(e.target.value)}
                style={{ width: 120 }}
                addonAfter="%"
              />
              {taxTreatment === 'sez_zero_rated' ? (
                <p className="text-xs text-ink-500 mt-1 mb-0">
                  Zero-rated treatment forces the rate to 0%.
                </p>
              ) : (
                <p className="text-xs text-ink-500 mt-1 mb-0">
                  Default 18%; matches the regular GST customer flow.
                </p>
              )}
            </div>

            <div>
              <p className="font-semibold text-ink-900 mb-1">
                Internal note <span className="text-ink-500 font-normal">(optional)</span>
              </p>
              <TextArea
                rows={2}
                maxLength={1000}
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="Why this tax decision (audit trail only — never shown to the customer)"
              />
            </div>
          </div>
        ) : null}
      </Modal>

      {/* ---- Reject modal ---- */}
      <Modal
        title="Reject SEZ payment"
        open={!!rejectTarget}
        confirmLoading={busy}
        onCancel={() => setRejectTarget(null)}
        onOk={doReject}
        okText="Reject"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        width={520}
      >
        {rejectTarget ? (
          <div>
            <p className="m-0 mb-3">
              Reject SEZ payment for{' '}
              <strong>{rejectTarget.legalName || rejectTarget.gstin}</strong>?
              The customer cannot proceed to payment until you change
              this decision.
            </p>
            <TextArea
              rows={3}
              maxLength={1000}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (shared with the customer)"
            />
          </div>
        ) : null}
      </Modal>

      {/* ---- View detail modal ---- */}
      <Modal
        title="SEZ pending signup detail"
        open={!!viewTarget}
        onCancel={() => setViewTarget(null)}
        footer={[
          <Button key="close" onClick={() => setViewTarget(null)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {viewTarget ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Pending signup id">
              <code>{viewTarget.pendingSignupId}</code>
            </Descriptions.Item>
            <Descriptions.Item label="GSTIN">
              <code>{viewTarget.gstin}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Legal name">
              {viewTarget.legalName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Trade name">
              {viewTarget.tradeName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer type">
              {customerTypeLabel(viewTarget.customerTaxType)}
            </Descriptions.Item>
            <Descriptions.Item label="State">
              {viewTarget.gstStateName
                ? `${viewTarget.gstStateName} (${viewTarget.gstStateCode || '—'})`
                : (viewTarget.gstStateCode || '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Contact">
              {viewTarget.contactName} — {viewTarget.contactEmail} —{' '}
              {viewTarget.contactMobile}
            </Descriptions.Item>
            <Descriptions.Item label="LOA / approval">
              {viewTarget.sezApprovalNumber || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Authorised operations">
              {viewTarget.sezAuthorisedOperationsConfirmed
                ? 'Confirmed'
                : 'Not confirmed'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {statusTag(viewTarget.sezPaymentApprovalStatus)}
            </Descriptions.Item>
            {viewTarget.sezSelectedTaxTreatment ? (
              <Descriptions.Item label="Selected tax treatment">
                {viewTarget.sezSelectedTaxTreatment === 'sez_zero_rated'
                  ? 'Zero-rated GST (0%)'
                  : `Taxable GST @ ${viewTarget.sezSelectedTaxPercent}%`}
              </Descriptions.Item>
            ) : null}
            {viewTarget.sezPaymentRejectReason ? (
              <Descriptions.Item label="Reject reason">
                {viewTarget.sezPaymentRejectReason}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Created at">
              {formatDateTime(viewTarget.createdAt) || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Expires at">
              {formatDateTime(viewTarget.expiresAt) || '—'}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </div>
  );
}
